/**
 * Summary Screen Component
 *
 * Displays weekly summary statistics and overall progress.
 * Celebrates patterns and achievements over time.
 */

import { useMemo, useState } from 'react';
import {
  TrendingUp,
  Award,
  Calendar,
  Sparkles,
  Clock,
  PieChart,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  useWeeklySummaryByDate,
  useOverallSummary,
  useStreak,
  CATEGORY_META,
  getWeekStart,
  getToday,
  type Category,
} from '@have-done/shared';

/**
 * Category color classes for the chart
 */
const CATEGORY_COLORS: Record<Category, string> = {
  work: 'bg-category-work',
  health: 'bg-category-health',
  creative: 'bg-category-creative',
  social: 'bg-category-social',
  personal: 'bg-category-personal',
  learning: 'bg-category-learning',
};

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  highlight?: boolean;
}

function StatCard({ label, value, icon, description, highlight }: StatCardProps) {
  return (
    <div
      className={`
        p-4 rounded-xl
        ${highlight ? 'bg-accent/10 border border-accent/30' : 'bg-bg-secondary'}
      `}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className={`${highlight ? 'text-accent' : 'text-text-tertiary'}`}>
          {icon}
        </span>
        <span className="text-sm text-text-secondary">{label}</span>
      </div>
      <p className={`text-2xl font-semibold ${highlight ? 'text-accent-dark' : 'text-text-primary'}`}>
        {value}
      </p>
      {description && (
        <p className="text-xs text-text-tertiary mt-1">{description}</p>
      )}
    </div>
  );
}

interface CategoryBreakdownProps {
  breakdown: Array<{ category: Category; count: number; percentage: number }>;
}

function CategoryBreakdown({ breakdown }: CategoryBreakdownProps) {
  if (breakdown.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-bg-tertiary">
        {breakdown.map(({ category, percentage }) => (
          <div
            key={category}
            className={`${CATEGORY_COLORS[category]} transition-all`}
            style={{ width: `${percentage}%` }}
            title={`${CATEGORY_META[category].label}: ${percentage.toFixed(0)}%`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {breakdown.map(({ category, count }) => (
          <div key={category} className="flex items-center gap-1.5">
            <span
              className={`w-2.5 h-2.5 rounded-full ${CATEGORY_COLORS[category]}`}
            />
            <span className="text-xs text-text-secondary">
              {CATEGORY_META[category].label} ({count})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatWeekRange(startDate: string): string {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return `${startMonth} - ${endMonth}`;
}

/**
 * Navigate to previous/next week
 */
function getAdjacentWeek(startDate: string, direction: 'prev' | 'next'): string {
  const date = new Date(startDate + 'T00:00:00');
  date.setDate(date.getDate() + (direction === 'prev' ? -7 : 7));
  // Format as YYYY-MM-DD in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function SummaryScreen() {
  // Current week (today's week) as reference
  const currentWeekStart = getWeekStart(getToday());

  // Selected week state (default to current week)
  const [selectedWeekStart, setSelectedWeekStart] = useState(currentWeekStart);

  // Check if viewing current week
  const isCurrentWeek = selectedWeekStart === currentWeekStart;

  // Navigate to previous week
  const goToPreviousWeek = () => {
    setSelectedWeekStart(getAdjacentWeek(selectedWeekStart, 'prev'));
  };

  // Navigate to next week
  const goToNextWeek = () => {
    setSelectedWeekStart(getAdjacentWeek(selectedWeekStart, 'next'));
  };

  // Go back to current week
  const goToCurrentWeek = () => {
    setSelectedWeekStart(currentWeekStart);
  };

  const weeklySummary = useWeeklySummaryByDate(selectedWeekStart);
  const overallSummary = useOverallSummary();
  const streak = useStreak();

  // Calculate category breakdown for visualization
  const categoryBreakdown = useMemo(() => {
    if (!weeklySummary?.categoryBreakdown || weeklySummary.categoryBreakdown.length === 0) return [];

    const total = weeklySummary.categoryBreakdown.reduce(
      (sum: number, item: { count: number }) => sum + item.count,
      0
    );
    if (total === 0) return [];

    return weeklySummary.categoryBreakdown.map(({ category, count }: { category: Category; count: number }) => ({
      category,
      count,
      percentage: (count / total) * 100,
    }));
  }, [weeklySummary?.categoryBreakdown]);

  // Loading state - skeleton mirrors actual layout structure exactly
  if (weeklySummary === undefined || overallSummary === undefined) {
    return (
      <div className="space-y-8 animate-pulse">
        {/* Header skeleton with week navigation */}
        <header className="space-y-2">
          <div className="h-8 w-44 bg-bg-tertiary rounded" />
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-bg-tertiary rounded-lg" />
            <div className="h-5 w-[140px] bg-bg-tertiary rounded" />
            <div className="h-9 w-9 bg-bg-tertiary rounded-lg" />
          </div>
        </header>

        {/* Weekly stats grid skeleton - matches StatCard structure */}
        <section>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-4 rounded-xl bg-bg-secondary">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-4 w-4 bg-bg-tertiary rounded" />
                  <div className="h-4 w-24 bg-bg-tertiary rounded" />
                </div>
                <div className="h-8 w-12 bg-bg-tertiary rounded" />
              </div>
            ))}
          </div>
        </section>

        {/* Category breakdown skeleton */}
        <section className="p-6 rounded-xl bg-bg-secondary">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-4 w-4 bg-bg-tertiary rounded" />
            <div className="h-4 w-36 bg-bg-tertiary rounded" />
          </div>
          <div className="h-3 w-full bg-bg-tertiary rounded-full mb-3" />
          <div className="flex flex-wrap gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-bg-tertiary rounded-full" />
                <div className="h-3 w-16 bg-bg-tertiary rounded" />
              </div>
            ))}
          </div>
        </section>

        {/* Overall stats skeleton */}
        <section className="p-6 rounded-xl bg-bg-secondary">
          <div className="h-6 w-20 bg-bg-tertiary rounded mb-4" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center p-4 rounded-lg bg-bg-primary">
                <div className="h-9 w-12 bg-bg-tertiary rounded mx-auto mb-1" />
                <div className="h-4 w-24 bg-bg-tertiary rounded mx-auto" />
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  const hasWeeklyData = weeklySummary && weeklySummary.totalEntries > 0;
  const hasOverallData = overallSummary && overallSummary.totalEntries > 0;

  return (
    <div className="space-y-8">
      {/* Header with week navigation */}
      <header className="space-y-2">
        <h2 className="text-2xl font-display font-semibold text-text-primary">
          Weekly Summary
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={goToPreviousWeek}
            className="p-2 rounded-lg hover:bg-bg-secondary text-text-secondary hover:text-text-primary transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <span className="text-text-secondary min-w-[140px] text-center">
            {formatWeekRange(selectedWeekStart)}
          </span>

          <button
            onClick={goToNextWeek}
            disabled={isCurrentWeek}
            className="p-2 rounded-lg hover:bg-bg-secondary text-text-secondary hover:text-text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Next week"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {!isCurrentWeek && (
            <button
              onClick={goToCurrentWeek}
              className="ml-2 px-3 py-1.5 text-sm rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              This Week
            </button>
          )}
        </div>
      </header>

      {/* Weekly stats */}
      <section aria-labelledby="weekly-stats-heading">
        <h3 id="weekly-stats-heading" className="sr-only">
          This week's statistics
        </h3>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            label="Accomplishments"
            value={weeklySummary?.totalEntries ?? 0}
            icon={<Calendar className="h-4 w-4" />}
          />
          <StatCard
            label="Big Wins"
            value={weeklySummary?.bigWins ?? 0}
            icon={<Sparkles className="h-4 w-4" />}
            highlight={(weeklySummary?.bigWins ?? 0) > 0}
          />
          <StatCard
            label="Active Days"
            value={weeklySummary?.dailyCounts?.length ?? 0}
            icon={<TrendingUp className="h-4 w-4" />}
            description="out of 7"
          />
          <StatCard
            label="Time Logged"
            value={formatMinutes(weeklySummary?.totalMinutes ?? 0)}
            icon={<Clock className="h-4 w-4" />}
          />
        </div>
      </section>

      {/* Category breakdown */}
      {hasWeeklyData && (
        <section
          className="p-6 rounded-xl bg-bg-secondary"
          aria-labelledby="category-breakdown-heading"
        >
          <h3
            id="category-breakdown-heading"
            className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2"
          >
            <PieChart className="h-4 w-4" />
            Category Breakdown
          </h3>
          <CategoryBreakdown breakdown={categoryBreakdown} />
        </section>
      )}

      {/* Overall stats */}
      {hasOverallData && (
        <section
          className="p-6 rounded-xl bg-bg-secondary"
          aria-labelledby="overall-stats-heading"
        >
          <h3
            id="overall-stats-heading"
            className="text-lg font-semibold text-text-primary mb-4"
          >
            All Time
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-bg-primary">
              <p className="text-3xl font-bold text-primary">
                {overallSummary.totalEntries}
              </p>
              <p className="text-sm text-text-secondary">Total accomplishments</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-bg-primary">
              <p className="text-3xl font-bold text-accent">
                {overallSummary.bigWins}
              </p>
              <p className="text-sm text-text-secondary">Big wins</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-bg-primary col-span-2 sm:col-span-1">
              <p className="text-3xl font-bold text-text-primary">
                {streak?.currentStreak ?? 0}
              </p>
              <p className="text-sm text-text-secondary">Current streak</p>
            </div>
          </div>

          {/* Average satisfaction */}
          {overallSummary.avgSatisfaction && (
            <div className="mt-4 p-4 rounded-lg bg-bg-primary">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Average satisfaction</span>
                <span className="text-lg font-semibold text-text-primary">
                  {Number.isInteger(overallSummary.avgSatisfaction) ? overallSummary.avgSatisfaction : overallSummary.avgSatisfaction.toFixed(1)} / 5
                </span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-bg-tertiary overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${(overallSummary.avgSatisfaction / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </section>
      )}

      {/* Empty state */}
      {!hasWeeklyData && !hasOverallData && (
        <div className="text-center py-12 rounded-xl bg-bg-secondary">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-bg-tertiary mb-4">
            <Award className="h-8 w-8 text-text-tertiary" />
          </div>
          <p className="text-text-secondary">No accomplishments logged yet</p>
          <p className="text-sm text-text-tertiary mt-1">
            Start logging your wins on the Today screen!
          </p>
        </div>
      )}

      {/* Encouragement */}
      {hasWeeklyData && (
        <footer className="text-center py-6 px-4 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
          <p className="text-text-primary font-medium">
            {weeklySummary.totalEntries >= 7
              ? 'Amazing week! You logged something every day.'
              : weeklySummary.totalEntries >= 3
              ? 'Great progress this week. Keep celebrating your wins!'
              : 'Every accomplishment counts. You\'re building something great.'}
          </p>
        </footer>
      )}
    </div>
  );
}
